import type { ComponentProps, ReactNode } from "react";
import {
  ChatImageDropZone,
  ChatImagePicker,
  PendingChatImage,
  pasteChatImages,
} from "@/core/components/chat-images";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { HarnessChatEmptyState } from "@/onboarding/components/harness-chat/harness-chat-empty-state";
import { HarnessChatInput } from "@/onboarding/components/harness-chat/harness-chat-input";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import { AccessDeniedState } from "./access-denied-state";
import { ChatBubble } from "./chat-bubble";
import { ChatPanelHeader } from "./chat-panel-header";
import { ChatThreadList } from "./chat-thread-list";
import { LiveAssistantMessage } from "./live-assistant-message";
import { SelectionContextChip } from "./selection-context-chip";
import type {
  AiScriptExplanationChatSidebarProps,
  ExplanationChatSurface,
} from "./types";
import { useAiScriptExplanationChatSession } from "./use-ai-script-explanation-chat-session";

const PAYROLL_SURFACE = {
  title: "Payroll chat",
  subtitle: "Chats about this pay period's results",
  emptyTitle: "Ask about this pay period",
  emptyDescription: "Find out who was paid what, and why.",
  composerPlaceholder: "Ask why a technician was paid a certain amount...",
  suggestedPrompts: [
    "Summarize this pay period's results",
    "Who earned the most this period, and why?",
    "Were any technicians paid the hourly floor instead of commission?",
  ],
};

const SelectionContextInputHeader = ({
  context,
  onDismiss,
}: {
  context: AiScriptChatSelectionContext | null;
  onDismiss?: () => void;
}) =>
  context ? (
    <div className="mb-2 flex">
      <SelectionContextChip context={context} onDismiss={onDismiss} />
    </div>
  ) : null;

type ChatImages = ReturnType<
  typeof useAiScriptExplanationChatSession
>["chatImages"];

/**
 * The composer, taking screenshots (picked, pasted, or dropped on the panel)
 * when the chat does.
 */
const Composer = ({
  chatImages,
  acceptsImages,
  header,
  ...input
}: ComponentProps<typeof HarnessChatInput> & {
  chatImages: ChatImages;
  acceptsImages: boolean;
  header: ReactNode;
}) => {
  if (!acceptsImages) return <HarnessChatInput {...input} header={header} />;
  return (
    <div
      className="contents"
      onPaste={(event) => pasteChatImages(event, chatImages.addFiles)}
    >
      <HarnessChatInput
        {...input}
        header={
          <>
            {header}
            {chatImages.images.length > 0 && (
              <Stack direction="row" gap={3} className="flex-wrap pt-2 pb-3">
                {chatImages.images.map((image, index) => (
                  <PendingChatImage
                    key={`${image.name}-${index}`}
                    image={image}
                    onRemove={() => chatImages.removeImage(image)}
                  />
                ))}
              </Stack>
            )}
          </>
        }
        actions={<ChatImagePicker iconOnly onFiles={chatImages.addFiles} />}
      />
    </div>
  );
};

const ImageDropZone = ({
  chatImages,
  acceptsImages,
  children,
}: {
  chatImages: ChatImages;
  acceptsImages: boolean;
  children: ReactNode;
}) =>
  acceptsImages ? (
    <ChatImageDropZone
      onFiles={chatImages.addFiles}
      className="h-full min-h-0 w-full"
    >
      {children}
    </ChatImageDropZone>
  ) : (
    children
  );

export const AiScriptExplanationChatSidebar = (
  props: AiScriptExplanationChatSidebarProps,
) => {
  const session = useAiScriptExplanationChatSession(props);
  return <AiScriptExplanationChatView {...props} session={session} />;
};

export const AiScriptExplanationChatView = ({
  session,
  title = PAYROLL_SURFACE.title,
  subtitle = PAYROLL_SURFACE.subtitle,
  emptyTitle,
  emptyDescription,
  composerPlaceholder = PAYROLL_SURFACE.composerPlaceholder,
  suggestedPrompts = PAYROLL_SURFACE.suggestedPrompts,
  acceptsImages = false,
  onClearSelectionContext,
  onClose,
}: ExplanationChatSurface & {
  session: ReturnType<typeof useAiScriptExplanationChatSession>;
  onClearSelectionContext?: () => void;
  onClose: () => void;
}) => {
  const {
    isAccessDenied,
    isListView,
    input,
    chatImages,
    imagePath,
    messages,
    threads,
    getIsThreadRunning,
    selectedThread,
    attachedContext,
    isBusy,
    isThreadHistoryLoading,
    displayedLiveText,
    progressSteps,
    bottomRef,
    textareaRef,
    handleDraftChange,
    handleOpenThread,
    handleNewChat,
    handleStartNewChatWithMessage,
    handleBackToList,
    handleSend,
  } = session;

  if (isAccessDenied) {
    return <AccessDeniedState onClose={onClose} />;
  }

  const isPayrollSurface = title === PAYROLL_SURFACE.title;
  const composerHeader = (
    <SelectionContextInputHeader
      context={attachedContext}
      onDismiss={onClearSelectionContext}
    />
  );

  const images = { chatImages, acceptsImages };

  if (isListView) {
    return (
      <ImageDropZone {...images}>
        <Stack align="stretch" className="h-full min-h-0 w-full" gap={0}>
          <ChatPanelHeader
            title={title}
            subtitle={subtitle}
            onNewChat={handleNewChat}
            onClose={onClose}
          />

          <ChatThreadList
            threads={threads}
            getIsThreadRunning={getIsThreadRunning}
            onOpenThread={handleOpenThread}
          />

          <Composer
            {...images}
            draft={input}
            disabled={isBusy}
            textareaRef={textareaRef}
            onDraftChange={handleDraftChange}
            onSend={() => handleStartNewChatWithMessage()}
            placeholder={composerPlaceholder}
            header={composerHeader}
          />
        </Stack>
      </ImageDropZone>
    );
  }

  return (
    <ImageDropZone {...images}>
      <Stack align="stretch" className="h-full min-h-0 w-full" gap={0}>
        <ChatPanelHeader
          title={selectedThread?.name ?? "New chat"}
          onBack={handleBackToList}
          onNewChat={handleNewChat}
          onClose={onClose}
        />

        <div
          data-chat-scroll-viewport
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <div className="p-4">
            {messages.length === 0 && !isBusy ? (
              isThreadHistoryLoading ? (
                <Stack gap={4} align="center" className="w-full py-8">
                  <Spinner layout="centered" />
                </Stack>
              ) : (
                <HarnessChatEmptyState
                  title={
                    emptyTitle ??
                    (isPayrollSurface ? PAYROLL_SURFACE.emptyTitle : title)
                  }
                  description={
                    emptyDescription ??
                    (isPayrollSurface
                      ? PAYROLL_SURFACE.emptyDescription
                      : subtitle)
                  }
                  prompts={suggestedPrompts}
                  disabled={isBusy}
                  onSend={handleSend}
                />
              )
            ) : (
              messages.map((message, index) => (
                <ChatBubble
                  key={message.id}
                  message={message}
                  prevRole={messages[index - 1]?.role}
                  imagePath={acceptsImages ? imagePath : undefined}
                />
              ))
            )}

            {/* Keep the live bubble mounted past isBusy until the typewriter
              finishes and the final message flushes into the transcript. */}
            {(isBusy || displayedLiveText.length > 0) && (
              <LiveAssistantMessage
                displayedLiveText={displayedLiveText}
                progressSteps={progressSteps}
              />
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        <Composer
          {...images}
          draft={input}
          disabled={isBusy}
          textareaRef={textareaRef}
          onDraftChange={handleDraftChange}
          onSend={() => handleSend()}
          placeholder={composerPlaceholder}
          header={composerHeader}
        />
      </Stack>
    </ImageDropZone>
  );
};
